import React, { 
  useState, 
  useEffect, 
  useRef } from 'react';
import { Rating } from 'react-simple-star-rating';
import { PaperAirplaneIcon } from '@heroicons/react/20/solid';
import { 
  checkReview, 
  submitReview } from '../../API/ServiceAPI';

import Modal from '../Modal';
import IconSM from "../../Assets/Images/ic_review.png";
import IconMD from "../../Assets/Images/ic_review@2x.png";
import IconLG from "../../Assets/Images/ic_review@3x.png";

const ReviewForm = ({ bookingID }) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [readOnly, setReadOnly] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const onSuccessHandler = (responseJSON) => {
      setData(responseJSON);
      setLoading(false);
    }
    const onFailureHandler = (messages) => {
      setLoading(false);
    }
    checkReview(bookingID, onSuccessHandler, onFailureHandler);
  }, [bookingID]);

  let ratingLapang = useRef(null);
  let ratingKebersihan = useRef(null);
  let ratingFasilitas = useRef(null);
  let ratingKeamanan = useRef(null);
  let ratingAkses = useRef(null);
  let review = useRef('');
  let isAnonymous = useRef(false);

  if (loading)
    return (
      <div className='flex justify-center items-center'>
        <div className='bg-white py-6 px-8 rounded-2xl w-auto'>Loading..</div>
      </div>
    )

  if (Object.keys(data).length === 0)
    return (
      <div className='flex justify-center items-center'>
        <div className='bg-white py-6 px-8 rounded-2xl max-w-full md:max-w-[850px]'>
          <div className="flex justify-between items-center gap-8">
            <div>
              <img src={IconMD} 
                srcSet={`${IconSM} 126w, ${IconMD} 252w, ${IconLG} 378w`} 
                sizes="(min-width: 600px) 150px, 100px" 
                alt="Review" 
                className="w-64" />
            </div>
            <div>
              <p className="font-bold text-2xl leading-[3rem] mb-2">
                Terima Kasih!
              </p>
              <p className="leading-relaxed mb-6">
                Ulasan anda untuk booking <span className='font-medium'>{bookingID}</span> sudah kami terima. 
                Terima Kasih telah membantu WCommunity menjadi lebih baik!
              </p>
              <a href='/'
                className='w-full md:w-auto bg-primaryRed text-white rounded-lg px-10 py-3 text-sm font-semibold'>
                  Kembali ke Beranda
              </a>
            </div>
          </div>
        </div>
      </div>
    )

  const setRatingLapang = (rate) => ratingLapang.current = rate
  const setRatingKebersihan = (rate) => ratingKebersihan.current = rate
  const setRatingFasilitas = (rate) => ratingFasilitas.current = rate
  const setRatingKeamanan = (rate) => ratingKeamanan.current = rate
  const setRatingAkses = (rate) => ratingAkses.current = rate

  const handleSubmitReview = () => {
    const params = {
      bookingID: data.service_member_book_id, 
      serviceId: data.service_id, 
      ratingLapang: ratingLapang.current, 
      ratingKebersihan: ratingKebersihan.current, 
      ratingFasilitas: ratingFasilitas.current, 
      ratingKeamanan: ratingKeamanan.current, 
      ratingAkses: ratingAkses.current, 
      review: review.current.value, 
      isAnonymous: isAnonymous.current.checked
    }

    const onSuccessHandler = (responseJSON) => {
      setShowModal(true);
      setReadOnly(true);
    }
    const onFailureHandler = (messages) => console.debug("FAIL", messages);
    submitReview(params, onSuccessHandler, onFailureHandler);
  }

  return (
    <div className='max-w-7xl bg-white rounded-2xl flex flex-col mx-auto p-8'>
      <Modal show={showModal}>
        <div className="flex justify-between items-center gap-8">
          <div>
            <img src={IconMD} 
              srcSet={`${IconSM} 126w, ${IconMD} 252w, ${IconLG} 378w`} 
              sizes="(min-width: 600px) 150px, 100px" 
              alt="Review" 
              className="w-64" />
          </div>
          <div>
            <p className="font-bold text-2xl leading-[3rem] mb-2">
              Terima Kasih, {data.contact_name}!
            </p>
            <p className="leading-relaxed mb-6">
              Ulasan anda akan kami teruskan kepada pihak pengelola {data.venue_name} sebagai bahan masukan. 
              Terima Kasih telah membantu WCommunity menjadi lebih baik!
            </p>
            <button 
              className='w-full md:w-auto bg-primaryRed text-white rounded-lg px-10 py-3 text-sm font-semibold' 
              onClick={(e) => setShowModal(false)}>
                Tutup
            </button>
          </div>
        </div>
      </Modal>
      <section className='relative aspect-[8/3] mb-4'>
        <div className='md:absolute md:inset-0 md:bottom-12'>
          <img src={data.pic_venue} className='w-full h-full object-cover rounded-t-2xl' />
        </div>
        <div className='md:absolute md:bottom-0 md:right-1/2 md:translate-x-1/2 md:min-h-[6rem] md:w-2/5 py-4 px-8 md:rounded-t-2xl md:bg-white text-center'>
          <h2 className='font-medium text-xl mb-0.5'>Ulas Lapang</h2>
          <h1 className='font-bold text-3xl'>{data.venue_name}</h1>
        </div>
      </section>
      <div className='md:w-3/4 mx-auto text-center mb-4'>
        <p className='leading-loose'>
          Ulasan anda dapat membantu {data.venue_name} untuk menjadi lebih baik lagi kedepannya. 
          Baik dari Fasilitas maupun Pelayanan, Terima Kasih!
        </p>
      </div>
      <div className='border-2 rounded-md p-2 md:p-6 mb-8 overflow-x-auto'>
        <div className='md:flex md:gap-10 md:justify-between'>
          <div className='mb-4 md:mb-0'>
            <dl className='md:grid md:grid-cols-2 md:gap-2'>
              <dt className='flex items-center font-semibold'>Kondisi Lapangan</dt>
              <dd className='mb-4 md:mb-0'><Rating onClick={setRatingLapang} readonly={readOnly} /></dd>
              <dt className='flex items-center font-semibold'>Kebersihan</dt>
              <dd className='mb-4 md:mb-0'><Rating onClick={setRatingKebersihan} readonly={readOnly} /></dd>
              <dt className='flex items-center font-semibold'>Kelengkapan Fasilitas</dt>
              <dd className='mb-4 md:mb-0'><Rating onClick={setRatingFasilitas} readonly={readOnly} /></dd>
              <dt className='flex items-center font-semibold'>Keamanan</dt>
              <dd className='mb-4 md:mb-0'><Rating onClick={setRatingKeamanan} readonly={readOnly} /></dd>
              <dt className='flex items-center font-semibold'>Akses Transportasi</dt>
              <dd className='mb-4 md:mb-0'><Rating onClick={setRatingAkses} readonly={readOnly} /></dd>
            </dl>
          </div>
          <div className='grow'>
            <textarea 
              className='w-full h-full bg-slate-200 resize-none rounded-2xl p-4' 
              placeholder='Berikan ulasan anda disini' ref={review} readOnly={readOnly} />
          </div>
        </div>
      </div>
      <div className='md:flex md:justify-between'>
        <div className='flex md:items-center mb-4 md:mb-0'>
          <input type='checkbox' id='isAnonymous' className='w-4 h-4 rounded translate-y-[3px] md:translate-y-0' ref={isAnonymous} disabled={readOnly} />
          <label htmlFor='isAnonymous' className='ml-2'>Samarkan nama sebagai Anonymous</label>
        </div>
        {(!readOnly) && (
          <button className='w-full md:w-auto flex justify-center items-center gap-2 bg-primaryRed text-white rounded-lg px-10 py-3 text-sm font-semibold' onClick={handleSubmitReview}>
            <PaperAirplaneIcon className='h-4' /> Kirim Ulasan
          </button>
        )}
      </div>
    </div>
  );
}

export default ReviewForm;